import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import type {PredictionConfig} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import {submitBulkPrediction} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import {
  getUseMSAForSubmission,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';

import {
  type ComponentKind,
  getComponentLabel,
  type PredictionComponent,
  type PredictionInput,
  normalizeValue,
} from './newPredictionState';

export interface BuildPredictionConfigsParams {
  inputs: Array<PredictionInput>;
  model: ScientificModelNode;
}

export interface RunMultiModelSubmitParams {
  inputs: Array<PredictionInput>;
  batchName: string;
  models: ReadonlyArray<ScientificModelNode>;
  // Per-model "Use MSAs" state keyed by externalReference. Only meaningful for OPTIONAL-MSA models;
  // getUseMSAForSubmission resolves REQUIRED→true and NONE→omitted regardless.
  msaEnabledByModel: Readonly<Record<string, boolean>>;
  // Every model's batch lands here. Omitted, the backend picks the folder (standalone only).
  folderId?: string;
}

// The generate-name endpoint returns "{Model Display Name} - {datetime}" (see
// benchling/model_hub/utils/name_generation.py). With several models selected, each batch is already
// prefixed with its own model at submit, so the embedded one has to go or the name would carry two
// model names — and the wrong one at that, since the default is generated off the first model only.
// Note the separators differ: the backend joins with " - " (hyphen), the submit prefix with " — "
// (em dash). A name that doesn't match the expected shape passes through untouched.
export function stripModelPrefix(defaultName: string, model: ScientificModelNode): string {
  const prefix = `${model.displayName} - `;
  return defaultName.startsWith(prefix) ? defaultName.slice(prefix.length) : defaultName;
}

export interface ResolveBatchNameParams {
  batchName: string;
  model: ScientificModelNode;
  modelCount: number;
}

/**
 * The name one model's batch will be created with. With several models the shared batch name is
 * prefixed with the model so the resulting batches are distinguishable; a single model keeps the
 * plain name. Exported so the builder can preview exactly what submit will send — the preview and
 * the submitted name must never drift apart.
 */
export function resolveBatchName({batchName, model, modelCount}: ResolveBatchNameParams): string {
  const trimmedBatchName = batchName.trim();
  if (modelCount <= 1 || trimmedBatchName === '') {
    return trimmedBatchName;
  }
  return `${model.displayName} — ${trimmedBatchName}`;
}

export interface ModelSubmitFailure {
  modelName: string;
  // The raw rejection, kept so the caller can classify it (credit error, structured user message, …).
  error: unknown;
}

export interface MultiModelSubmitResult {
  succeeded: number;
  // Models whose batch creation failed. The loop never aborts early, so a partial success still
  // submits every other model.
  failures: Array<ModelSubmitFailure>;
}

interface RawComponentConfig {
  _type: ComponentKind;
  value: string;
  name: string;
}

/**
 * Expand one component into `copies` raw configs. The backend config schema has no stoichiometry
 * field, so N copies are materialized as N repeated entries. Each copy gets a numbered name suffix
 * (`Chain A-1`, `Chain A-2`, …); a single copy keeps the plain label (`Chain A`).
 */
function expandComponent(component: PredictionComponent, label: string): Array<RawComponentConfig> {
  const value = normalizeValue(component);
  const copies = Math.max(1, Math.floor(component.copies));
  if (copies === 1) {
    return [{_type: component.type, value, name: label}];
  }
  return Array.from(
    {length: copies},
    (_unused, copyIndex): RawComponentConfig => ({
      _type: component.type,
      value,
      name: `${label}-${copyIndex + 1}`,
    })
  );
}

/** Map a single input's components to one prediction config (one PredictionJob). */
function buildPredictionConfig(
  components: Array<PredictionComponent>,
  model: ScientificModelNode
): PredictionConfig {
  if (!model.capabilities.supportsMultimers) {
    // Monomer models submit a single component and ignore copies.
    const monomer = components[0];
    return {
      component: {
        _type: monomer.type,
        value: normalizeValue(monomer),
        name: getComponentLabel(components, 0),
      },
    };
  }
  const rawComponents = components.flatMap((component, index) =>
    expandComponent(component, getComponentLabel(components, index))
  );
  return {components: rawComponents};
}

export function buildPredictionConfigs({
  inputs,
  model,
}: BuildPredictionConfigsParams): Array<PredictionConfig> {
  return inputs.map((input) => buildPredictionConfig(input.components, model));
}

/**
 * Submit the same inputs against every selected model: one prediction batch per model (M inputs × N
 * models → N batches, each with M jobs). Each model's MSA value resolves independently. A model's
 * failure is recorded and the loop continues, so a partial failure still submits the rest. Mirrors
 * the data-first flow's submission semantics.
 */
export async function runMultiModelSubmit({
  inputs,
  batchName,
  models,
  msaEnabledByModel,
  folderId,
}: RunMultiModelSubmitParams): Promise<MultiModelSubmitResult> {
  // Names are always populated (defaults are assigned at create/import time), so the persisted job
  // name is just the trimmed input name.
  const jobNames = inputs.map((input) => input.name.trim());
  let succeeded = 0;
  const failures: Array<ModelSubmitFailure> = [];

  for (const model of models) {
    try {
      const name = resolveBatchName({batchName, model, modelCount: models.length});
      await submitBulkPrediction({
        model,
        predictionConfigs: buildPredictionConfigs({inputs, model}),
        jobNames,
        modelParams: {
          useMsa: getUseMSAForSubmission(model, msaEnabledByModel[model.externalReference] ?? true),
        },
        source: MODEL_RUN_SOURCE.MODEL_HUB_UI,
        ...(name ? {name} : {}),
        ...(folderId != null ? {folderId} : {}),
      });
      succeeded++;
      // Multi-model submit intentionally collects each model's failure and continues, so a partial
      // failure still submits the rest (mirrors the data-first flow). The caller surfaces the
      // collected failures, so nothing is silently swallowed.
      // eslint-disable-next-line benchlint/always-throw-from-catch -- see comment above
    } catch (submitError) {
      failures.push({modelName: model.displayName, error: submitError});
    }
  }

  return {succeeded, failures};
}
